import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  TextField,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  GridRenderEditCellParams,
  useGridApiContext
} from '@mui/x-data-grid';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import TimesheetViewControls, {
  getStartEndDateStrings,
  type ViewScale
} from './TimesheetViewControls';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { getUserTimezone } from '../utils/dateUtils';
import { fromDateTimeLocal } from '../utils/dateUtils';
import api from '../services/api';

/** Format ISO string as date + time in 24h (yyyy-MM-dd HH:mm) in local time */
function formatDateTime24(iso: string): string {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'yyyy-MM-dd HH:mm');
  } catch {
    return '';
  }
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected'
};

interface TaskOption {
  id: string;
  name: string;
  accountId: string;
  account?: { id: string; name: string; company?: { id: string; name: string } };
}

interface TimesheetEntryRow {
  id: string;
  organizationId: string;
  taskId: string;
  startTime: string;
  stopTime: string;
  hours?: number;
  description: string;
  /** API returns `status`; legacy code may use approvalStatus */
  status?: string;
  approvalStatus?: string;
  task?: { id: string; name: string; account?: { id: string; name: string; company?: { id: string; name: string } } };
  _isNew?: boolean;
}

function getStatus(row: TimesheetEntryRow): string {
  return row.status ?? row.approvalStatus ?? 'draft';
}

interface TimesheetTableViewProps {
  organizationId: string;
  onRefresh?: () => void;
}

function EditStatusCell(props: GridRenderEditCellParams) {
  const { id, field, row } = props;
  const apiRef = useGridApiContext();
  const rawStatus = (row as TimesheetEntryRow).status ?? (row as TimesheetEntryRow).approvalStatus ?? 'draft';
  const [status, setStatus] = useState(rawStatus);

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setStatus(newValue);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <FormControl size="small" fullWidth>
      <Select value={status} onChange={handleChange} label="Status" variant="standard">
        <MenuItem value="draft">Draft</MenuItem>
        <MenuItem value="submitted">Submitted</MenuItem>
        <MenuItem value="approved">Approved</MenuItem>
        <MenuItem value="rejected">Rejected</MenuItem>
      </Select>
    </FormControl>
  );
}

function EditTaskCell(props: GridRenderEditCellParams & { tasks: TaskOption[] }) {
  const { id, field, row, tasks } = props;
  const apiRef = useGridApiContext();
  const rowTaskId = (row as TimesheetEntryRow).taskId || '';
  const [taskId, setTaskId] = useState(rowTaskId);

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setTaskId(newValue);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <FormControl size="small" fullWidth>
      <Select value={taskId} onChange={handleChange} label="Task" variant="standard" displayEmpty>
        <MenuItem value="">
          <em>Select task</em>
        </MenuItem>
        {tasks.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.name}
            {t.account?.name && ` (${t.account.name})`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function EditDescriptionCell(props: GridRenderEditCellParams) {
  const { id, field, row } = props;
  const apiRef = useGridApiContext();
  const rowDesc = (row as TimesheetEntryRow).description ?? '';
  const [text, setText] = useState(rowDesc);

  return (
    <TextField
      size="small"
      fullWidth
      value={text}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        apiRef.current.setEditCellValue({ id, field, value: v });
      }}
      variant="standard"
      multiline
      minRows={1}
    />
  );
}

/** Edit cell: Start or Stop with separate date + time inputs (24h) */
function EditStartStopCell(props: GridRenderEditCellParams & { type: 'start' | 'stop' }) {
  const { id, field, row } = props;
  const apiRef = useGridApiContext();
  const { preferences } = useUserPreferences();
  const isoValue = props.type === 'start' ? (row as TimesheetEntryRow).startTime : (row as TimesheetEntryRow).stopTime;
  let dateStr = '';
  let timeStr = '';
  if (isoValue) {
    try {
      const d = parseISO(isoValue);
      dateStr = format(d, 'yyyy-MM-dd');
      timeStr = format(d, 'HH:mm');
    } catch {
      // leave empty
    }
  }
  const [date, setDate] = useState(dateStr);
  const [time, setTime] = useState(timeStr);

  const pushValue = (newDate: string, newTime: string) => {
    if (newDate && newTime) {
      const iso = fromDateTimeLocal(`${newDate}T${newTime}`, preferences);
      apiRef.current.setEditCellValue({ id, field, value: iso });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
      <TextField
        size="small"
        type="date"
        value={date}
        onChange={(e) => {
          const v = e.target.value;
          setDate(v);
          pushValue(v, time);
        }}
        variant="standard"
        InputLabelProps={{ shrink: true }}
        inputProps={{ style: { fontSize: '0.8rem' } }}
      />
      <TextField
        size="small"
        type="time"
        value={time}
        onChange={(e) => {
          const v = e.target.value;
          setTime(v);
          pushValue(date, v);
        }}
        variant="standard"
        InputLabelProps={{ shrink: true }}
        inputProps={{ style: { fontSize: '0.8rem' } }}
      />
    </Box>
  );
}

export default function TimesheetTableView({ organizationId, onRefresh }: TimesheetTableViewProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { preferences, getPreference, setPreference } = useUserPreferences();
  const timezone = getUserTimezone(preferences);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewScale, setViewScale] = useState<ViewScale>(() => {
    if (isMobile) return '1-day';
    try {
      return (getPreference('timesheet.table.viewScale', 'week') as ViewScale) || 'week';
    } catch {
      return 'week';
    }
  });
  const [entries, setEntries] = useState<TimesheetEntryRow[]>([]);
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newRowId, setNewRowId] = useState<number>(0);

  const { startDate, endDate } = useMemo(
    () => getStartEndDateStrings(currentDate, viewScale),
    [currentDate, viewScale]
  );

  const loadEntries = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.getTimesheetEntries({
        organizationId,
        startDate,
        endDate,
        timezone
      });
      const data = (response.data ?? response) as TimesheetEntryRow[];
      const sorted = [...data].sort((a, b) => {
        const tA = a.startTime ? new Date(a.startTime).getTime() : 0;
        const tB = b.startTime ? new Date(b.startTime).getTime() : 0;
        return tA - tB;
      });
      setEntries(sorted);
    } catch (err: any) {
      console.error('Error loading timesheet entries:', err);
      setError(err.response?.data?.message || 'Failed to load entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, startDate, endDate, timezone]);

  const loadTasks = useCallback(async () => {
    if (!organizationId) return;
    try {
      const accountsRes = await api.getAccounts({ organizationId, isActive: 'true' });
      const accounts = accountsRes.data || [];
      const allTasks: TaskOption[] = [];
      for (const acc of accounts) {
        const tasksRes = await api.getTasks({ accountId: acc.id, organizationId });
        const list = (tasksRes.data || []).map((t: any) => ({
          ...t,
          account: acc
        }));
        allTasks.push(...list);
      }
      setTasks(allTasks);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    }
  }, [organizationId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!isMobile) {
      setPreference('timesheet.table.viewScale', viewScale, 'string');
    }
  }, [viewScale, isMobile, setPreference]);

  const handleJumpToNow = () => {
    setCurrentDate(new Date());
  };

  const handleProcessRowUpdate = async (newRow: TimesheetEntryRow, oldRow: TimesheetEntryRow) => {
    if (!newRow.taskId) {
      throw new Error('Task is required');
    }
    if (!(newRow.description ?? '').trim()) {
      throw new Error('Description is required');
    }
    if (!newRow.startTime || !newRow.stopTime) {
      throw new Error('Start and Stop date/time are required');
    }
    const isNew = String(newRow.id).startsWith('new-');
    const status = getStatus(newRow);
    try {
      if (isNew) {
        const payload = {
          organizationId,
          taskId: newRow.taskId,
          startTime: newRow.startTime,
          stopTime: newRow.stopTime,
          description: newRow.description || '',
          approvalStatus: status || 'draft'
        };
        const res = await api.createTimesheetEntry(payload);
        const created = res?.data ?? res;
        await loadEntries();
        onRefresh?.();
        return { ...created, id: created.id };
      } else {
        const payload = {
          taskId: newRow.taskId,
          startTime: newRow.startTime,
          stopTime: newRow.stopTime,
          description: newRow.description ?? ''
        };
        await api.updateTimesheetEntry(newRow.id, payload);
        await loadEntries();
        onRefresh?.();
        return newRow;
      }
    } catch (err: any) {
      throw err;
    }
  };

  const handleDelete = async (id: string) => {
    if (String(id).startsWith('new-')) {
      setEntries((prev) => prev.filter((r) => r.id !== id));
      return;
    }
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await api.deleteTimesheetEntry(id);
      await loadEntries();
      onRefresh?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete entry');
    }
  };

  const handleAddEntry = () => {
    const startTime = new Date();
    startTime.setHours(9, 0, 0, 0);
    const stopTime = new Date();
    stopTime.setHours(10, 0, 0, 0);
    const firstTaskId = tasks[0]?.id || '';
    const row: TimesheetEntryRow = {
      id: `new-${Date.now()}-${newRowId}`,
      organizationId,
      taskId: firstTaskId,
      startTime: startTime.toISOString(),
      stopTime: stopTime.toISOString(),
      description: '',
      status: 'draft',
      _isNew: true
    };
    setNewRowId((n) => n + 1);
    setEntries((prev) => [...prev, row].sort((a, b) => {
      const tA = a.startTime ? new Date(a.startTime).getTime() : 0;
      const tB = b.startTime ? new Date(b.startTime).getTime() : 0;
      return tA - tB;
    }));
  };

  const getDurationHours = (startTime: string, stopTime: string) => {
    if (!startTime || !stopTime) return 0;
    try {
      const start = parseISO(startTime);
      const stop = parseISO(stopTime);
      return (differenceInMinutes(stop, start) / 60);
    } catch {
      return 0;
    }
  };

  const columns = useMemo(
    (): GridColDef<TimesheetEntryRow>[] => [
      {
        field: 'startTime',
        headerName: 'Start',
        width: 165,
        type: 'string',
        valueGetter: (params: GridValueGetterParams<TimesheetEntryRow>) =>
          formatDateTime24(params.row.startTime ?? ''),
        editable: true,
        renderEditCell: (params) => <EditStartStopCell {...params} type="start" />
      },
      {
        field: 'stopTime',
        headerName: 'Stop',
        width: 165,
        type: 'string',
        valueGetter: (params: GridValueGetterParams<TimesheetEntryRow>) =>
          formatDateTime24(params.row.stopTime ?? ''),
        editable: true,
        renderEditCell: (params) => <EditStartStopCell {...params} type="stop" />
      },
      {
        field: 'duration',
        headerName: 'Duration',
        width: 90,
        valueGetter: (params: GridValueGetterParams<TimesheetEntryRow>) => {
          const h = getDurationHours(params.row.startTime, params.row.stopTime);
          return h ? `${h.toFixed(1)}h` : '';
        },
        sortable: false,
        editable: false
      },
      {
        field: 'company',
        headerName: 'Company',
        width: 130,
        valueGetter: (params: GridValueGetterParams<TimesheetEntryRow>) =>
          params.row.task?.account?.company?.name ?? '',
        sortable: false,
        editable: false
      },
      {
        field: 'account',
        headerName: 'Account',
        width: 130,
        valueGetter: (params: GridValueGetterParams<TimesheetEntryRow>) =>
          params.row.task?.account?.name ?? '',
        sortable: false,
        editable: false
      },
      {
        field: 'taskId',
        headerName: 'Task',
        width: 180,
        type: 'string',
        valueGetter: (params: GridValueGetterParams<TimesheetEntryRow>) => params.row.task?.name ?? '',
        editable: true,
        renderEditCell: (params) => <EditTaskCell {...params} tasks={tasks} />
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 160,
        type: 'string',
        editable: true,
        renderEditCell: (params) => <EditDescriptionCell {...params} />
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        type: 'string',
        valueGetter: (params: GridValueGetterParams<TimesheetEntryRow>) =>
          STATUS_LABELS[getStatus(params.row)] ?? getStatus(params.row),
        editable: true,
        renderEditCell: (params) => <EditStatusCell {...params} />,
        renderCell: (params) => {
          const status = getStatus(params.row);
          return (
            <Chip
              size="small"
              label={STATUS_LABELS[status] ?? status}
              color={
                status === 'approved' ? 'success' :
                status === 'rejected' ? 'error' :
                status === 'submitted' ? 'warning' : 'default'
              }
            />
          );
        }
      },
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleDelete stable, columns for display only
    [tasks]
  );

  if (!organizationId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Select an organization to view timesheets.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <TimesheetViewControls
        currentDate={currentDate}
        viewScale={viewScale}
        onDateChange={setCurrentDate}
        onViewScaleChange={setViewScale}
        onJumpToNow={handleJumpToNow}
        showResolution={false}
      />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
          >
            Add entry
          </Button>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 300 }}>
            <DataGrid
              rows={entries}
              columns={columns}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              processRowUpdate={handleProcessRowUpdate}
              onProcessRowUpdateError={(err) => setError(err?.message || 'Failed to save')}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'startTime', sort: 'asc' }]
                },
                pagination: { paginationModel: { pageSize: 25 } }
              }}
              pageSizeOptions={[10, 25, 50]}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': { borderBottom: 1, borderColor: 'divider' },
                '& .MuiDataGrid-columnHeaders': { borderBottom: 1, borderColor: 'divider' }
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
